<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKanban } from 'dashboard/composables/useKanban';
import KanbanPipelineList from './KanbanPipelineList.vue';
import KanbanPipelineModal from './components/KanbanPipelineModal.vue';

const { t } = useI18n();
const { createPipeline, updatePipeline, deletePipeline } = useKanban();

const showCreateModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const selectedPipeline = ref(null);
const pipelineListRef = ref(null);

const handleCreatePipelineEvent = () => {
  selectedPipeline.value = null;
  showCreateModal.value = true;
};

const handleEditPipelineEvent = pipeline => {
  selectedPipeline.value = pipeline;
  showEditModal.value = true;
};

const handleDeletePipelineEvent = pipeline => {
  selectedPipeline.value = pipeline;
  showDeleteModal.value = true;
};

const handleCloseModal = () => {
  showCreateModal.value = false;
  showEditModal.value = false;
  selectedPipeline.value = null;
};

const handleCloseDeleteModal = () => {
  showDeleteModal.value = false;
  selectedPipeline.value = null;
};

const handleCreatePipeline = async formData => {
  try {
    await createPipeline(formData);
    showCreateModal.value = false;
    // Recarregar a lista
    if (pipelineListRef.value) {
      await pipelineListRef.value.loadPipelines();
    }
  } catch (error) {
    console.error('Error creating pipeline:', error);
    // TODO: Show error message to user
  }
};

const handleUpdatePipeline = async formData => {
  try {
    await updatePipeline(selectedPipeline.value.id, formData);
    showEditModal.value = false;
    selectedPipeline.value = null;
    // Recarregar a lista
    if (pipelineListRef.value) {
      await pipelineListRef.value.loadPipelines();
    }
  } catch (error) {
    console.error('Error updating pipeline:', error);
    // TODO: Show error message to user
  }
};

const handleConfirmDelete = async () => {
  try {
    await deletePipeline(selectedPipeline.value.id);
    showDeleteModal.value = false;
    selectedPipeline.value = null;
    // Recarregar a lista
    if (pipelineListRef.value) {
      await pipelineListRef.value.loadPipelines();
    }
  } catch (error) {
    console.error('Error deleting pipeline:', error);
    // TODO: Show error message to user
  }
};
</script>

<template>
  <div class="w-full h-full">
    <KanbanPipelineList
      ref="pipelineListRef"
      @create-pipeline="handleCreatePipelineEvent"
      @edit-pipeline="handleEditPipelineEvent"
      @delete-pipeline="handleDeletePipelineEvent"
    />

    <!-- Create Pipeline Modal -->
    <KanbanPipelineModal
      v-if="showCreateModal"
      :pipeline="null"
      :is-edit="false"
      @close="handleCloseModal"
      @save="handleCreatePipeline"
    />

    <!-- Edit Pipeline Modal -->
    <KanbanPipelineModal
      v-if="showEditModal"
      :pipeline="selectedPipeline"
      :is-edit="true"
      @close="handleCloseModal"
      @save="handleUpdatePipeline"
    />

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      @click.self="handleCloseDeleteModal"
    >
      <div
        class="bg-n-background border border-n-weak rounded-xl shadow-xl max-w-md w-full mx-4 p-6"
      >
        <div class="flex items-center gap-3 mb-4">
          <div
            class="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center"
          >
            <i class="i-lucide-trash-2 size-5 text-red-600" />
          </div>
          <div>
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ t('KANBAN.DELETE_MODAL.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11 mt-1">
              {{ t('KANBAN.DELETE_MODAL.SUBTITLE') }}
            </p>
          </div>
        </div>

        <div class="bg-n-slate-2 rounded-lg p-4 mb-6">
          <p class="text-sm text-n-slate-12">
            <strong>{{ selectedPipeline?.name }}</strong>
          </p>
          <p
            v-if="selectedPipeline?.description"
            class="text-xs text-n-slate-11 mt-1"
          >
            {{ selectedPipeline.description }}
          </p>
        </div>

        <p class="text-sm text-n-slate-11 mb-6">
          {{ t('KANBAN.DELETE_MODAL.WARNING') }}
        </p>

        <div class="flex gap-3 justify-end">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-n-slate-12 bg-n-background border border-n-weak rounded-lg hover:bg-n-slate-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            @click="handleCloseDeleteModal"
          >
            {{ t('KANBAN.DELETE_MODAL.CANCEL') }}
          </button>
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
            @click="handleConfirmDelete"
          >
            {{ t('KANBAN.DELETE_MODAL.CONFIRM') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
